<template>
  <Message
    v-if="props.data.disclaimer"
    :pt-options="{
      variant: 'infoBanner'
    }"
    class="mb-6"
  >
    <div
      v-html="props.data.disclaimer"
    ></div>
  </Message>

  <div
    v-if="projectsStatus === 'pending' && !projectaData?.data?.projects?.length"
    class="flex flex-wrap -mx-3 -my-3"
  >
    <div
      v-for="n in 6"
      :key="n"
      class="px-3 py-3 w-1/2 flex-[1_0_50%]"
    >
      <Skeleton
        class="!w-full !h-[16rem]"
      >
      </Skeleton>
    </div>
  </div>

  <template v-if="projectaData?.data">
    <template v-if="projectaData?.data?.projects?.length">
      <div class="grid grid-cols-2 gap-6">
        <div
          v-for="project in projectaData.data.projects"
          :key="`project_short_${project.id}`"
          class="flex flex-col justify-between w-full h-full p-2 bg-white border border-gray-300 rounded shadow-md hover:border-secondary hover:text-secondary"
        >
          <div class="relative mb-4">
            <div class="relative flex justify-center items-center overflow-hidden w-full pt-[70%] rounded">
              <nuxt-picture
                preload
                :img-attrs="{class: 'absolute top-0 left-0 w-full h-full object-cover'}"
                :src="project.image ? project.image : '/img/missing.png'"
                :placeholder="[100, 50, 10]"
                sizes=""
              />
            </div>

            <div
              v-if="project.is_stock"
              class="absolute bottom-0 left-0 w-full px-4 py-1 text-white bg-primary text-sm"
            >
              <template v-if="project.stock_count > 0">
                {{ project.stock_count }} {{ $t('pcs') }} {{ $t('inStock') }}
              </template>
              <template v-else>
                {{ $t('outOfStock') }}
              </template>
            </div>
          </div>

          <div
            v-if="!project.projectIsActive"
            class="pl-2 mb-2 text-xs text-red-500 text-center"
          >
            <span
              v-html="$t('projectNotAvailable')"
            ></span>
          </div>

          <div>
            <label v-show="props.data.type === 'EM' || props.data.type === 'RP'">
              <fieldset class="flex items-center mb-2">
                <Checkbox
                  v-model="selectedItemsIds"
                  :disabled="props.data.disabled || !project.projectIsActive"
                  :value="project.id"
                  class="projectCheckbox small"
                />
                <span class="pl-2 text-xs font-bold">{{ project.name }}</span>
              </fieldset>
            </label>

            <label>
              <fieldset class="flex items-center pt-2">
                <RadioButton
                  v-model="selectedItemFeatured"
                  :disabled="props.data.disabled || !project.projectIsActive"
                  :value="project.id"
                  class="projectCheckbox small"
                />
                <span
                  v-if="props.data.type === 'EM' || props.data.type === 'RP'"
                  class="pl-2 text-xs"
                >
                  <span>{{ $t('featured') }}</span>
                </span>
                <span
                  v-else
                  class="pl-2 text-xs font-bold"
                >
                  {{ project.name }}
                </span>
              </fieldset>
            </label>
          </div>

          <div class="mt-4 text-right">
            <div>
              <span class="font-medium">{{ moneyFormat(project.total) }}</span>
            </div>
            <div class="text-xs text-gray-500">
              {{ $t('shippingAndTaxes') }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-center pt-4">
        <WidgetsSystemProductsLoadMore
          v-if="projectaData.data && (projectaData?._meta?.links?.limit * projectaData?._meta?.links?.page) < projectaData?._meta?.links?.total
            && projectaData?._meta?.links?.limit < projectaData?._meta?.links?.total"
          :autoloading="true"
          :is-load-more="isLoadMore"
          :params-products="paramsProjects"
          :products-pending="projectsStatus === 'pending'"
          :products="projectaData"
          @load-more-products="e => {
            paramsProjects.page = e.page
            isLoadMore = true

            isUpdateParams = true
            refresh().then(() => {
              isUpdateParams = false
              isLoadMore = false
            })
          }"
        />
      </div>
    </template>

    <template v-else>
      <Message
        severity="info"
      >
        {{ $t('projectsIsEmptyForGifts') }}
      </Message>
    </template>
  </template>
</template>

<script setup>
const { getProjects, paramsProjects } = useCustomerProjects({ key: 'short' })

const selectedItemsIds = ref([])
const selectedItemFeatured = ref(false)
const isLoadMore = ref(false)

const returnData = ref({})

/**
* Props
*/
const props = defineProps({
  id: {
    type: [String],
    default: null
  },
  data: {
    type: [Object],
    default: null
  }
})

const alertForActive = ref(props.data?.additionalOptions?.showAlert ?? false)

/**
* Emits
*/
const emit = defineEmits(['returnData', 'isPending', 'has-alcohol-changed'])

/**
* Selected Items
*/
if (Array.isArray(props.data?.formData[props.data.name])) {
  const selectedProjectArray = (props.data?.availableProjects) 
    ? props.data.formData[props.data.name].filter(n => props.data?.availableProjects.includes(n)) 
    : props.data.formData[props.data.name] 

  selectedItemsIds.value = selectedProjectArray.filter(n => n !== 0 && n !== 'undefined' && n !== undefined)

  selectedItemFeatured.value = props.data.formData[props.data.secondName] 
    ? props.data.formData[props.data.secondName] 
    : false
}

/**
* Get Projects
*/
paramsProjects.value = {
  limit: 35,
  page: 1,
  filters: {
    isSlp: true,
    isShort: true,
    selected_ids: selectedItemsIds.value
  }
}

const { data: projectaData, refresh, status: projectsStatus } = getProjects()
const { $i18n: i18n } = useNuxtApp()
const toast = useToast()

/*
* Update selected projects when data changing
*/
watch(projectaData, (newA) => {
  if (!newA && paramsProjects.value.page === 1) {
    return
  }

  const selectedProjectArray = newA.data.projects.filter(n => selectedItemsIds.value.includes(n.id) && n !== 0 && n.projectIsActive)

  selectedItemsIds.value = selectedProjectArray.map(n => n.id)

  selectedItemFeatured.value = selectedProjectArray.find(n => n === selectedItemFeatured.value)
    ? selectedItemFeatured.value
    : selectedItemsIds.value[0]
}, { deep: true, immediate: true })

/*
* Block "Done" button if projects is loading
*/
watch(projectsStatus, (newA) => {
  emit('isPending', newA === 'pending')
}, { deep: true, immediate: true })

/**
* Selectes Names
*/
const selectedItems = computed({
  get() {
    const namesArr = []

    if (projectaData.value?.data?.projects) {
      selectedItemsIds.value.forEach((id) => { 
        const item = projectaData.value?.data?.projects?.find(n => n.id === id)

        namesArr.push(item)
      })
    }
    return namesArr
  }
})

/**
* checks if all selected projects have premade greeting cards
*/
const allPremadeGreetingCards = computed({
  get() {
    let allPremadeGreetingCard = true
    selectedItemsIds.value.forEach((id) => { 
      if(!projectaData.value?.data?.projects?.find(n => n.id === id)?.premade_greeting_card) {
        allPremadeGreetingCard = false
      }
    })

    return allPremadeGreetingCard
  }
})

/**
* Change Selected Items
*/

watch(selectedItemsIds, (newA, oldA) => {
  // Featured
  if (newA.length) {
    if (!selectedItemFeatured.value) {
      selectedItemFeatured.value = newA[0]
    } else if (!newA.includes(selectedItemFeatured.value)) {
      selectedItemFeatured.value = newA[0]
    }
  } else {
    selectedItemFeatured.value = false
  }

  // Show Toast if customer unchecks a project in an active campaign or redeem
  const unchecked = oldA.filter(id => !newA.includes(id))
  if (alertForActive.value && unchecked.length > 0) {
    alertForActive.value = false // do not show alert again
    toast.add({
      severity: 'warn',
      detail: (props.data.type === 'RP') ? i18n?.t('changeProjectRedeemWarning') : i18n?.t('changeProjectCampaignWarning'),
      life: 5000
    })
  }

  // Return data
  returnData.value[props.data.name] = selectedItemsIds.value
  returnData.value[props.data.secondName] = selectedItemFeatured.value

  if (props.data.additionalFields?.find(n => n.name === 'selectedProjects')) {
    returnData.value['selectedProjects'] = selectedItems.value
  }

  if (props.data.additionalFields?.find(n => n.name === 'all_premade_greeting_card')) {
    returnData.value.all_premade_greeting_card = allPremadeGreetingCards.value
  }

  // Alcohol check
  let hasAlcohol = false
  if (Array.isArray(newA) && props.data?.availableProjects) {
    for (const id of newA) {
      const project = projectaData.value?.data?.projects.find(p => p.id === id)
      if (project?.has_alcohol) {
        hasAlcohol = true
        break
      }
    }
  }
  returnData.value.hasAlcohol = hasAlcohol

  emit('returnData', returnData.value)
}, { deep: true })

/**
* Change Featured Item
*/
watch([selectedItemFeatured, projectsStatus], ([newA, newB]) => {
  if (newA && newB === 'success') {
    if (props.data.type === 'EM' || props.data.type === 'RP') {
      if (!selectedItemsIds.value.includes(newA) && newA) {
        selectedItemsIds.value.push(newA)
      }
    } else {
      selectedItemsIds.value = [newA]
    }

    returnData.value[props.data.name] = selectedItemsIds.value
    returnData.value[props.data.secondName] = selectedItemFeatured.value

    if (props.data.additionalFields?.find(n => n.name === 'selectedProjects')) {
      returnData.value['selectedProjects'] = selectedItems.value
    }

    if (props.data.additionalFields?.find(n => n.name === 'all_premade_greeting_card')) {
      returnData.value.all_premade_greeting_card = allPremadeGreetingCards.value
    }

    emit('returnData', returnData.value)
  }
}, { deep: true })
</script>